/**
 * Service for launch monitor data import (image OCR + CSV parsing).
 *
 * Uses the existing axios instance from recommendationService which has
 * the JWT auth interceptor attached.
 */

import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;

export interface ExtractedField {
  value: number;
  confidence: number;   // 0–1
  source_label: string; // original column header
  /** Unit printed on the source before conversion, e.g. 'km/h'. */
  source_unit?: string | null;
  /** F6: the raw OCR read before auto-correction. Set only when a correction fired. */
  original_value?: number | null;
  /** F6: which auto-correction rule fired — 'decimal' | 'digit_swap' | 'sign'. */
  correction_rule?: string | null;
}

/**
 * A field where the Haiku cross-check read something other than the OCR pass.
 * `reason` is 'sign' when the two disagree about a minus sign — the failure
 * that scores as high confidence and that no range check can see.
 */
export interface FieldDisagreement {
  ocr_value: number;
  llm_value: number;
  reason: 'sign' | 'value';
  tolerance: number;
  source_unit?: string | null;
}

/**
 * Why the cross-check produced (or did not produce) a result.
 *
 * `null` means no cross-check was attempted. It must NOT be rendered as
 * agreement — 'agreed' is the only value that means a second reader confirmed
 * the numbers.
 */
export type LLMCrosscheckStatus =
  | 'not_entitled'       // account is not on the Advanced-Haiku tier
  | 'not_configured'     // entitled, but the service has no key
  | 'nothing_to_compare' // OCR found nothing to check
  | 'agreed'
  | 'disagreed'
  | 'failed';

export interface ParsedLaunchMonitorData {
  success: boolean;
  extraction_method: string;   // "template" | "generic" | "csv" | "llm_vision"
  template_matched: string | null;
  template_confidence: number;
  club_type: string;
  shot_count: number;
  fields: Record<string, ExtractedField>;
  unmapped_fields: Record<string, number | null>;
  warnings: string[];
  needs_llm_fallback: boolean;
  /** 'imperial' | 'metric' | 'mixed' | 'unknown' — what the device displayed. */
  source_units?: string;
  field_disagreements?: Record<string, FieldDisagreement>;
  llm_crosscheck_status?: LLMCrosscheckStatus | null;
}

export type MonitorBrand = 'trackman' | 'gcquad' | 'mevo_plus' | 'garmin' | 'skytrak';

export async function parseLaunchMonitorImage(
  imageFile: File,
  clubType: 'driver' | 'iron' | 'wedge',
  monitorBrand?: MonitorBrand,
): Promise<ParsedLaunchMonitorData> {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('club_type', clubType);
  if (monitorBrand) formData.append('monitor_brand', monitorBrand);

  const response = await axios.post<ParsedLaunchMonitorData>(
    `${API_BASE}/advanced/launch-monitor/parse-image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function parseLaunchMonitorCSV(
  csvFile: File,
  clubType: 'driver' | 'iron' | 'wedge',
  monitorBrand?: MonitorBrand,
): Promise<ParsedLaunchMonitorData> {
  const formData = new FormData();
  formData.append('csv_file', csvFile);
  formData.append('club_type', clubType);
  if (monitorBrand) formData.append('monitor_brand', monitorBrand);

  const response = await axios.post<ParsedLaunchMonitorData>(
    `${API_BASE}/advanced/launch-monitor/parse-csv`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}
